import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, MenuItem, TextField } from "@mui/material";
import ColorizeIcon from "@mui/icons-material/Colorize";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { SectionShell } from "../../common/dashboard/SectionShell";
import { fieldSx } from "../../common/dashboard/formStyles";
import { emailImageAccept } from "./emailAssetUpload";
import {
  EmailBuilderState,
  UpdateEmailBuilderState,
} from "./emailBuilderData";
import { EmailFormState } from "./emailMockData";
import {
  Field,
  UpdateEmailField,
  fieldHelperClassName,
  fieldLabelClassName,
  inputClassName,
  stepDescriptionClassName,
  stepTitleClassName,
  textareaClassName,
} from "./emailWizardShared";

export type StepTwoDetailsProps = {
  email: EmailFormState;
  isBusy: boolean;
  isSentRecord: boolean;
  updateEmailField: UpdateEmailField;
  updateBuilderState: UpdateEmailBuilderState;
  uploadImage: (file: File) => Promise<string>;
};

const subHeadingClassName = "text-[20px] font-extrabold leading-tight text-gray-950";
const smallActionButtonClassName =
  "inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-4 text-[14px] font-extrabold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60";
const smallDangerActionButtonClassName =
  "inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-red-300 bg-white px-4 text-[14px] font-extrabold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Color helpers for the palette picker (Step 2 ANNOTATION 1 — binding: full
// picker with gradient area, eyedropper, hue + alpha sliders, HEX and R/G/B).
// ---------------------------------------------------------------------------

type HsvaColor = { h: number; s: number; v: number; a: number };

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const parseHexColor = (
  value: string
): { r: number; g: number; b: number; a: number } | null => {
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
    a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
  };
};

const channelToHex = (channel: number) =>
  clampNumber(Math.round(channel), 0, 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();

const rgbaToHexColor = (r: number, g: number, b: number, a: number) =>
  `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}${
    a >= 1 ? "" : channelToHex(a * 255)
  }`;

const rgbToHsv = (r: number, g: number, b: number) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

type EyeDropperResult = { sRGBHex: string };
type EyeDropperConstructor = new () => { open: () => Promise<EyeDropperResult> };

const getEyeDropperConstructor = (): EyeDropperConstructor | null =>
  typeof window !== "undefined" && "EyeDropper" in window
    ? (window as unknown as { EyeDropper: EyeDropperConstructor }).EyeDropper
    : null;

const colorSliderCss = `
.cmp-color-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 9999px;
  outline: none;
}
.cmp-color-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
.cmp-color-slider::-moz-range-thumb {
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
`;

const ColorPickerPopover = ({
  color,
  onChange,
}: {
  color: string;
  onChange: (hex: string) => void;
}) => {
  const initial = parseHexColor(color) || { r: 0, g: 0, b: 0, a: 1 };
  const [hsva, setHsva] = useState<HsvaColor>(() => ({
    ...rgbToHsv(initial.r, initial.g, initial.b),
    a: initial.a,
  }));
  const [hexDraft, setHexDraft] = useState(() =>
    rgbaToHexColor(initial.r, initial.g, initial.b, initial.a)
  );

  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  const roundedRgb = {
    r: Math.round(rgb.r),
    g: Math.round(rgb.g),
    b: Math.round(rgb.b),
  };
  const currentHex = rgbaToHexColor(
    roundedRgb.r,
    roundedRgb.g,
    roundedRgb.b,
    hsva.a
  );
  const eyeDropperSupported = !!getEyeDropperConstructor();

  const applyHsva = (next: HsvaColor) => {
    setHsva(next);
    const nextRgb = hsvToRgb(next.h, next.s, next.v);
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, next.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyRgbChannel = (channel: "r" | "g" | "b", value: number) => {
    const nextRgb = {
      ...roundedRgb,
      [channel]: clampNumber(Math.round(value) || 0, 0, 255),
    };
    setHsva({ ...rgbToHsv(nextRgb.r, nextRgb.g, nextRgb.b), a: hsva.a });
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, hsva.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyHexDraft = (value: string) => {
    setHexDraft(value);
    const parsed = parseHexColor(value);
    if (!parsed) return;
    setHsva({ ...rgbToHsv(parsed.r, parsed.g, parsed.b), a: parsed.a });
    onChange(rgbaToHexColor(parsed.r, parsed.g, parsed.b, parsed.a));
  };

  const pickWithEyeDropper = async () => {
    const EyeDropperCtor = getEyeDropperConstructor();
    if (!EyeDropperCtor) return;
    try {
      const result = await new EyeDropperCtor().open();
      applyHexDraft(result.sRGBHex);
    } catch {
      // Color picking was canceled.
    }
  };

  const updateSaturationFromPointer = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    applyHsva({
      ...hsva,
      s: clampNumber((event.clientX - bounds.left) / bounds.width, 0, 1),
      v: clampNumber(1 - (event.clientY - bounds.top) / bounds.height, 0, 1),
    });
  };

  return (
    <div className="absolute left-0 top-full z-30 mt-2 w-[260px] rounded-md border border-gray-200 bg-white p-3 shadow-xl">
      <style>{colorSliderCss}</style>
      <div
        className="relative h-40 w-full cursor-crosshair rounded"
        role="presentation"
        style={{
          background: `linear-gradient(to top, #000000, rgba(0, 0, 0, 0)), linear-gradient(to right, #ffffff, hsl(${Math.round(
            hsva.h
          )}, 100%, 50%))`,
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateSaturationFromPointer(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) updateSaturationFromPointer(event);
        }}
      >
        <span
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{
            left: `${hsva.s * 100}%`,
            top: `${(1 - hsva.v) * 100}%`,
            backgroundColor: `rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})`,
          }}
        />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={pickWithEyeDropper}
          disabled={!eyeDropperSupported}
          title={
            eyeDropperSupported
              ? "Pick a color from the screen"
              : "Color picking from the screen is not supported in this browser"
          }
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-[#2F6FED] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ColorizeIcon fontSize="small" />
        </button>
        <div className="flex-1 space-y-2.5">
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={Math.round(hsva.h)}
            aria-label="Hue"
            onChange={(event) =>
              applyHsva({ ...hsva, h: Number(event.target.value) })
            }
            className="cmp-color-slider w-full"
            style={{
              background:
                "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
            }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(hsva.a * 100)}
            aria-label="Opacity"
            onChange={(event) =>
              applyHsva({ ...hsva, a: Number(event.target.value) / 100 })
            }
            className="cmp-color-slider w-full"
            style={{
              background: `linear-gradient(to right, rgba(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b}, 0), rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})), repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 12px 12px`,
            }}
          />
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <label className="block flex-1">
          <span className="text-[11px] font-extrabold uppercase text-gray-500">
            HEX
          </span>
          <input
            value={hexDraft}
            onChange={(event) => applyHexDraft(event.target.value)}
            onBlur={() => setHexDraft(currentHex)}
            className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-[13px] text-gray-950 outline-none focus:border-cpaGold"
          />
        </label>
        {(["r", "g", "b"] as const).map((channel) => (
          <label key={channel} className="block w-[52px]">
            <span className="text-[11px] font-extrabold uppercase text-gray-500">
              {channel.toUpperCase()}
            </span>
            <input
              type="number"
              min={0}
              max={255}
              value={roundedRgb[channel]}
              onChange={(event) =>
                applyRgbChannel(channel, Number(event.target.value))
              }
              className="mt-1 h-9 w-full rounded border border-gray-300 px-1.5 text-center text-[13px] text-gray-950 outline-none focus:border-cpaGold"
            />
          </label>
        ))}
      </div>
    </div>
  );
};

const ThemeColorSwatch = ({
  label,
  color,
  disabled,
  onChange,
}: {
  label: string;
  color: string;
  disabled: boolean;
  onChange: (hex: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Shared dropdown behavior (SH-005): close on outside click and Escape like
  // every other anchored menu in the dashboard.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        disabled={disabled}
        title={label}
        aria-label={label}
        className="flex h-[64px] w-[64px] items-center justify-center rounded-md border border-gray-300 bg-white transition hover:border-cpaGold disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          className="block h-7 w-7 rounded-[4px] border border-gray-200"
          style={{ backgroundColor: color }}
        />
      </button>
      {open ? <ColorPickerPopover color={color} onChange={onChange} /> : null}
    </div>
  );
};

// POL-020 (Greeting Line): the open menu lists EXACTLY the three binding
// options (no injected placeholder row — FormSelectField would add a 4th
// disabled MenuItem, so the TextField select is used directly). "none" maps
// to "" at the call site so displayEmpty shows the gray placeholder.
const greetingLineOptions: Array<{
  value: Exclude<EmailBuilderState["greetingLine"], "none">;
  label: string;
}> = [
  { value: "firstName", label: "First Name" },
  { value: "fullName", label: "First and Last Name" },
  { value: "companyName", label: "Company Name" },
];

// Swatch order mirrors the Step 2 mock: blue, light blue, black, white.
const themeSwatches: Array<{
  field: keyof EmailBuilderState["theme"];
  label: string;
}> = [
  { field: "highlightColor", label: "Highlight color" },
  { field: "textColor", label: "Text color" },
  { field: "footerBackgroundColor", label: "Footer background color" },
  { field: "backgroundColor", label: "Background color" },
];

const StepTwoDetails = ({
  email,
  isBusy,
  isSentRecord,
  updateEmailField,
  updateBuilderState,
  uploadImage,
}: StepTwoDetailsProps) => {
  const [logoError, setLogoError] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const isLocked = isBusy || isSentRecord;

  const handleLogoFile = async (file?: File | null) => {
    if (!file) return;
    setLogoError("");
    setIsUploadingLogo(true);
    try {
      const logoUrl = await uploadImage(file);
      updateBuilderState((currentState) => ({ ...currentState, logoUrl }));
    } catch (error) {
      setLogoError(
        error instanceof Error
          ? error.message
          : "The logo could not be uploaded. Please try again."
      );
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setLogoError("");
    updateBuilderState((currentState) => ({ ...currentState, logoUrl: "" }));
  };

  const greetingPreview =
    email.builderState.greetingLine === "fullName"
      ? "Preview: Hi {First and Last Name},"
      : email.builderState.greetingLine === "companyName"
        ? "Preview: Hi {Company Name},"
        : "Preview: Hi {First Name},";

  return (
    <div className="space-y-8">
      <div>
        <h2 className={stepTitleClassName}>Campaign Details</h2>
        <p className={stepDescriptionClassName}>
          Provide the key details for your communication, including the campaign
          name, subject line, sender information, and preview text. These details
          help improve email deliverability, increase open rates, and create a
          more professional experience for recipients.
        </p>
      </div>
      {/* B10 (POL-002 Emails leg): shared SectionShell card with the
          transcription-bound numbered gold badge (variant="numbered"). */}
      <SectionShell
        id="email-step2-basic-information"
        variant="numbered"
        number={1}
        title="Basic Information"
        description="Give your campaign a name and add a short description for internal use only"
      >
        <div className="space-y-8">
          <Field
            label="Campaign Name"
            helper="For internal use. Name for this campaign (not visible to recipients)."
            required
          >
            <input
              value={email.category}
              onChange={(event) => updateEmailField("category", event.target.value)}
              className={inputClassName}
              disabled={isLocked}
              placeholder="June Chamber Newsletter"
            />
          </Field>
          <Field
            label="Campaign Description (Optional)"
            helper="Add specific details for internal reference only."
          >
            <textarea
              value={email.seoDescription}
              onChange={(event) =>
                updateEmailField("seoDescription", event.target.value)
              }
              className={textareaClassName}
              disabled={isLocked}
              placeholder="Monthly newsletter featuring upcoming events, member highlights, and chamber updates."
            />
          </Field>
        </div>
      </SectionShell>
      <SectionShell
        id="email-step2-email-settings"
        variant="numbered"
        number={2}
        title="Email Settings"
        description="Define the key email details your recipients will see."
      >
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="lg:col-span-2">
            <Field
              label="Email Subject Line"
              helper="Keep it clear, compelling and under 60 characters for the best open rates."
              required
            >
              <input
                value={email.subject}
                onChange={(event) =>
                  updateEmailField("subject", event.target.value.slice(0, 60))
                }
                maxLength={60}
                className={inputClassName}
                disabled={isLocked}
                placeholder="Enter Subject"
              />
              <p className="mt-2 text-sm text-black">
                {email.subject.length}/60 characters
              </p>
            </Field>
          </div>
          <div className="lg:col-span-2">
            <Field
              label="Preview Text (Preheader)"
              helper={
                <>
                  This appears after the subject line in most email inboxes.{" "}
                  <strong>
                    The pre-header is the first thing recipients see before
                    opening an email, make it compelling to encourage readers to
                    open.
                  </strong>
                </>
              }
            >
              <input
                value={email.preheader}
                onChange={(event) =>
                  updateEmailField("preheader", event.target.value.slice(0, 100))
                }
                maxLength={100}
                className={inputClassName}
                disabled={isLocked}
                placeholder="Enter Preheader Details"
              />
              <p className="mt-2 text-sm text-black">
                {email.preheader.length}/100 characters
              </p>
            </Field>
          </div>
          <Field
            label="From Name"
            required
            helper="The name recipients will see in their inbox."
          >
            <input
              value={email.fromName}
              onChange={(event) => updateEmailField("fromName", event.target.value)}
              className={inputClassName}
              disabled={isLocked}
              placeholder="Enter Chamber or Contact Name"
            />
          </Field>
          <Field
            label="From Email Address"
            required
            helper="The email recipients will see in their inbox."
          >
            <input
              value={email.fromEmail}
              onChange={(event) => updateEmailField("fromEmail", event.target.value)}
              className={inputClassName}
              disabled={isLocked}
              placeholder="Enter Chamber or Contact Email Address"
            />
            {email.senderReadinessDetail ? (
              <p className="mt-2 text-[13px] leading-5 text-gray-500">
                {email.senderReadinessDetail}
              </p>
            ) : null}
          </Field>
          <div className="lg:col-span-2">
            <Field
              label="Reply-To Email (Optional)"
              helper="Replies to this email will go to this address if different than sent from email address"
            >
              <input
                value={email.replyToEmail}
                onChange={(event) =>
                  updateEmailField("replyToEmail", event.target.value)
                }
                className={inputClassName}
                disabled={isLocked}
                placeholder="Enter Alternative Reply-To Email"
              />
            </Field>
          </div>
        </div>
      </SectionShell>
      <SectionShell
        id="email-step2-branding-personalization"
        variant="numbered"
        number={3}
        title="Branding & Personalization"
        description="Maintain a consistent brand experience and connect with your audience."
      >
        <div className="space-y-8">
          <div>
            <span className={fieldLabelClassName}>Chamber Logo</span>
            <span className={fieldHelperClassName}>
              Upload an image up to 20MB. It will be converted and optimized for email.
            </span>
            <div className="mt-3">
              <input
                ref={logoInputRef}
                type="file"
                accept={emailImageAccept}
                className="hidden"
                disabled={isLocked || isUploadingLogo}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  void handleLogoFile(file);
                }}
              />
              {email.builderState.logoUrl ? (
                <div className="rounded-md border border-gray-300 bg-white px-6 py-5">
                  <div className="flex flex-col items-center gap-4">
                    <img
                      src={email.builderState.logoUrl}
                      alt="Chamber logo preview"
                      className="max-h-24 max-w-[220px] object-contain"
                    />
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        type="button"
                        className={smallActionButtonClassName}
                        onClick={() => logoInputRef.current?.click()}
                        disabled={isLocked || isUploadingLogo}
                      >
                        {isUploadingLogo ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        Replace Logo
                      </button>
                      <button
                        type="button"
                        className={smallDangerActionButtonClassName}
                        onClick={removeLogo}
                        disabled={isLocked || isUploadingLogo}
                      >
                        Delete Logo
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isLocked || isUploadingLogo}
                  className="flex min-h-[150px] w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 bg-white px-6 py-8 text-gray-500 transition hover:border-cpaGold hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isUploadingLogo ? (
                    <>
                      <CircularProgress color="inherit" size={26} />
                      <span className="text-[15px]">Uploading logo...</span>
                    </>
                  ) : (
                    <>
                      <FileUploadOutlinedIcon
                        className="text-gray-400"
                        sx={{ fontSize: 34 }}
                      />
                      <span className="text-[15px]">Click Upload Logo</span>
                    </>
                  )}
                </button>
              )}
              {logoError ? (
                <p className="mt-2 text-sm text-red-600">{logoError}</p>
              ) : null}
            </div>
          </div>
          <div>
            <h3 className={subHeadingClassName}>Default Content Theme</h3>
            <div className="mt-4">
              <span className={fieldLabelClassName}>Color Palette</span>
              <span className={fieldHelperClassName}>
                This color palette will be reflected in the email templates, you
                can click on the color to edit them.
              </span>
              <div className="mt-3 flex flex-wrap gap-4">
                {themeSwatches.map((swatch) => (
                  <ThemeColorSwatch
                    key={swatch.field}
                    label={swatch.label}
                    color={email.builderState.theme[swatch.field]}
                    disabled={isLocked}
                    onChange={(hex) =>
                      updateBuilderState((currentState) => ({
                        ...currentState,
                        theme: {
                          ...currentState.theme,
                          [swatch.field]: hex,
                        },
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <div>
            <h3 className={subHeadingClassName}>Personalization</h3>
            <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div>
                <span className="text-[15px] leading-6 text-gray-950">
                  Greeting Line
                </span>
                <div className="mt-3">
                  {/* POL-020: MUI TextField select (platform select look);
                      closed field shows the gray placeholder via displayEmpty
                      + renderValue, open menu lists only the three binding
                      options. */}
                  <TextField
                    select
                    fullWidth
                    value={
                      email.builderState.greetingLine === "none"
                        ? ""
                        : email.builderState.greetingLine
                    }
                    onChange={(event) =>
                      updateBuilderState((currentState) => ({
                        ...currentState,
                        greetingLine: (event.target.value ||
                          "none") as EmailBuilderState["greetingLine"],
                      }))
                    }
                    disabled={isLocked}
                    sx={fieldSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selectedValue) => {
                        const value = selectedValue as string;
                        if (!value) {
                          return (
                            <span className="text-gray-500">
                              Select Greeting Line
                            </span>
                          );
                        }
                        return (
                          greetingLineOptions.find(
                            (option) => option.value === value
                          )?.label || value
                        );
                      },
                    }}
                  >
                    {greetingLineOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
              <div className="flex items-end">
                {/* h-14 (56px) matches the MUI outlined select height so the
                    Preview box stays flush beside the new field (POL-020). */}
                <div className="flex h-14 w-full items-center rounded-md bg-gray-100 px-4 text-[15px] text-gray-600">
                  {greetingPreview}
                </div>
              </div>
            </div>
          </div>
          <div>
            <span className={fieldLabelClassName}>Footer Text</span>
            <span className={fieldHelperClassName}>
              Enter the text you want to show on the footer.
            </span>
            <div className="mt-3 rounded-md border border-gray-300 bg-white px-6 py-6 text-center">
              <textarea
                value={email.builderState.footerText}
                onChange={(event) =>
                  updateBuilderState((currentState) => ({
                    ...currentState,
                    footerText: event.target.value,
                  }))
                }
                rows={2}
                disabled={isLocked}
                placeholder={`© ${new Date().getFullYear()} {INSERT CHAMBER NAME}. All rights reserved.`}
                className="w-full resize-none border-0 bg-transparent text-center text-[15px] text-gray-500 outline-none placeholder:text-gray-400 disabled:text-gray-400"
              />
              <p className="mt-2 text-[15px] text-gray-500">
                Click Here to <span className="underline">Unsubscribe</span>
              </p>
              <p className="mt-2 text-[15px] text-gray-500">
                Add Social Media Logos
              </p>
            </div>
          </div>
        </div>
      </SectionShell>
    </div>
  );
};

export default StepTwoDetails;
